import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import SortArrowIcon from '../icons/SortArrowIcon';
import NamedBox from '../atoms/NamedBox';
import { useTableSort } from '../hooks/useTableSort';
import { SpecialTeamsParticipationsRow } from '../types/CollegeProductionData';
import { COLOR_BACKGROUND_WARM, COLOR_BACKGROUND_LIGHT, COLOR_MIDDLE_GRAY, COLOR_TEXT, COLOR_WHITE, COLOR_BLUE } from '../styles/colors';

type ColumnAlign = 'center' | 'left';

interface ColumnConfig {
  key: keyof SpecialTeamsParticipationsRow;
  label: string;
  align: ColumnAlign;
}

const COLUMNS: ColumnConfig[] = [
  { key: 'year', label: 'Year', align: 'center' },
  { key: 'specialTeamsType', label: 'Special Teams Phase', align: 'left' },
  { key: 'playCount', label: 'Play Count', align: 'center' },
];

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  headerName: {
    fontWeight: 700,
  },
  tableWrapper: {
    width: '100%',
    overflowX: 'auto' as const,
    backgroundColor: '#ffffff',
  },
  table: {
    width: '100%',
    minWidth: 400,
    borderCollapse: 'collapse' as const,
    border: `1px solid ${COLOR_BACKGROUND_WARM}`,
    fontSize: theme.typography.pxToRem(16),
  },
  theadRow: {
    height: 40,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e0e0e0',
  },
  th: {
    overflow: 'hidden',
    border: `1px solid ${COLOR_BACKGROUND_WARM}`,
    borderTop: `2px solid ${COLOR_MIDDLE_GRAY}`,
    padding: theme.spacing(1),
    whiteSpace: 'nowrap' as const,
    color: COLOR_TEXT,
    fontWeight: 700,
    textAlign: 'left' as const,
    userSelect: 'none' as const,
    cursor: 'pointer',

    '&:first-child': {
      width: 80,
      minWidth: 80,
    },

    '&:last-child': {
      borderRight: 'none',
    },
  },
  sortLabel: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.25),
  },
  sortIcon: {
    opacity: 1,
    color: COLOR_MIDDLE_GRAY,

    '& svg': {
      display: 'block',
      width: 9,
      height: 'auto',
    },
  },
  alignCenter: {
    textAlign: 'center' as const,
  },
  alignLeft: {
    textAlign: 'left' as const,
  },
  bodyRow: {
    height: 44,
    backgroundColor: COLOR_WHITE,

    '&:nth-child(odd)': {
      backgroundColor: COLOR_BACKGROUND_LIGHT,
    },
  },
  td: {
    fontWeight: 600,
    color: '#1e1e1e',
    padding: theme.spacing(0.75, 1),
    whiteSpace: 'nowrap' as const,
    borderRight: '1px solid #f0f0f0',

    '&:last-child': {
      borderRight: 'none',
    },
  },
}), { name: 'SpecialTeamsPlayerParticipationTable' });

interface SpecialTeamsPlayerParticipationTableProps {
  className?: string;
  data?: SpecialTeamsParticipationsRow[];
}

export default function SpecialTeamsPlayerParticipationTable(props: SpecialTeamsPlayerParticipationTableProps) {
  const { className, data } = props;
  const classes = useStyles();
  const { sortKey, sortDirection, handleSort, sortedRows } = useTableSort<SpecialTeamsParticipationsRow, keyof SpecialTeamsParticipationsRow>({
    rows: data,
    initialSortKey: 'year',
    initialDirection: 'desc',
    nextKeyDirection: 'desc',
  });

  return (
    <NamedBox
      className={clsx(classes.root, className)}
      headerNameClassName={classes.headerName}
      name="Special Teams Player Participation"
    >
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead>
            <tr className={classes.theadRow}>
              {COLUMNS.map(column => {
                const alignClass = column.align === 'left' ? classes.alignLeft : classes.alignCenter;
                const isActive = column.key === sortKey;
                return (
                  <th key={column.key} className={clsx(classes.th, alignClass)}>
                    <TableSortLabel
                      active={isActive}
                      direction={isActive ? sortDirection : 'asc'}
                      className={classes.sortLabel}
                      hideSortIcon={false}
                      IconComponent={(iconProps) => (
                        <SortArrowIcon
                          {...iconProps}
                          className={`${iconProps.className} ${classes.sortIcon}`}
                          style={{
                            color: isActive ? COLOR_BLUE : '#b5b5b5',
                          }}
                        />
                      )}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, index) => (
              <tr key={`${row.year}-${row.specialTeamsType}-${index}`} className={classes.bodyRow}>
                {COLUMNS.map(column => {
                  const alignClass = column.align === 'left' ? classes.alignLeft : classes.alignCenter;
                  return (
                    <td key={column.key} className={clsx(classes.td, alignClass)}>
                      {row[column.key]}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </NamedBox>
  );
}
